import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

type MetaAttribute = 'name' | 'property';

export interface SeoConfig {
  title: string;
  description: string;
  keywords?: string[];
  robots?: string;
  image?: string;
  siteName?: string;
  type?: string;
  twitterHandle?: string;
  url?: string;
  canonicalPath?: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly renderer: Renderer2;
  private readonly defaultOrigin = 'https://framework.sparkbusinesstechnology.com.au';

  constructor(
    private readonly meta: Meta,
    private readonly title: Title,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  updateTags(config: SeoConfig): void {
    const pageUrl = config.url ?? this.resolveUrl(config.canonicalPath);

    this.title.setTitle(config.title);
    this.setMetaTag('description', config.description);
    this.setMetaTag('keywords', config.keywords?.join(', '));
    this.setMetaTag('robots', config.robots ?? 'index,follow');
    this.setMetaTag('author', 'Spark Business Technology');

    const ogImage = config.image ?? this.resolveUrl('/assets/social/spark-framework-og.svg');
    const ogType = config.type ?? 'website';
    const siteName = config.siteName ?? 'Spark Server Framework';

    this.setMetaTag('og:title', config.title, 'property');
    this.setMetaTag('og:description', config.description, 'property');
    this.setMetaTag('og:type', ogType, 'property');
    this.setMetaTag('og:url', pageUrl, 'property');
    this.setMetaTag('og:site_name', siteName, 'property');
    this.setMetaTag('og:image', ogImage, 'property');

    this.setMetaTag('twitter:card', 'summary_large_image');
    this.setMetaTag('twitter:title', config.title);
    this.setMetaTag('twitter:description', config.description);
    this.setMetaTag('twitter:image', ogImage);
    if (config.twitterHandle) {
      this.setMetaTag('twitter:site', config.twitterHandle);
      this.setMetaTag('twitter:creator', config.twitterHandle);
    }

    this.setCanonical(pageUrl);
  }

  setJsonLd(scriptId: string, schema: Record<string, unknown>): void {
    if (!schema) {
      return;
    }

    const target = this.document.head || this.document.body;
    if (!target) {
      return;
    }

    const existing = this.document.getElementById(scriptId);
    if (existing) {
      existing.textContent = JSON.stringify(schema);
      return;
    }

    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'type', 'application/ld+json');
    this.renderer.setAttribute(script, 'id', scriptId);
    this.renderer.appendChild(target, script);
    this.renderer.setProperty(script, 'text', JSON.stringify(schema));
  }

  removeJsonLd(scriptId: string): void {
    const existing = this.document.getElementById(scriptId);
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }
  }

  resolveUrl(path?: string): string {
    const origin = this.document?.location?.origin ?? this.defaultOrigin;
    if (!path) {
      const currentPath = this.document?.location?.pathname ?? '/';
      return this.joinUrl(origin, currentPath);
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    return this.joinUrl(origin, path);
  }

  private joinUrl(origin: string, path: string): string {
    const normalizedOrigin = origin.replace(/\/$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedOrigin}${normalizedPath}`;
  }

  private setCanonical(url: string): void {
    if (!url) {
      return;
    }

    const head = this.document.head;
    if (!head) {
      return;
    }

    let link = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.appendChild(head, link);
    }

    this.renderer.setAttribute(link, 'href', url);
  }

  private setMetaTag(name: string, content?: string, attr: MetaAttribute = 'name'): void {
    if (!content) {
      return;
    }
    this.meta.updateTag({ [attr]: name, content });
  }
}
